/*! webmd.visit */

/**
 * This JS will track a number of items pertaining to a user's visit via session JSON cookie, 'vtime' property. 
 * For now, the only thing it actually will do, is before a pageview ping a cookie to see if we are in the middle
 * of an active session. Currently a session is defined as 30 minutes and set as a default paramater. 
 * If a session has been idle for over 30 minutes, we will start a new session on the next pageview.
 * 
 * NOTE! The logic to set isNewVisit to true or false is purely triggered by pageviews. This means:
 *    - webmd.visit does not use any timers, so a user in an active visit that is sitting on a page 
 *      for 2 hours with no pageviews triggered will still be in an exisiting, non-new visit
 *    - dynamic web pages must be certain to trigger page views before refreshing / updating any 
 *      other connected content to make certain webmd.visit is reporting accurately on visit status
 * 
 * @namespace
 * @author <a href="mailto:mfaletti@webmd.net">Michael Faletti</a>
 * @require webmd.cookie
 * @version 3
 */

import cookie from 'webmd-cookie';
import debug from 'webmd-debug';
import { getSLD } from 'webmd-url';

export let isNewVisit;
export let visitView;

if ( typeof window !== 'undefined' ) {

  /*==CONFIG================================================*/
  
  /**
   * Length of a session in minutes
   * @type {integer}
   */
  let lengthOfSessionInMinutes = 30;
  
  /**
   * Cookie name where we will store the session timestamp. For now we reuse 
   * the ui cookie, but we might want to put this somewhere else later
   * @type {string}
   */
  let cookieName = 'ui';

  /**
   * local variable to keep track of whether we are in a first page view of a visit or not
   * initially is null before init
   * @type {boolean}
   */
  let _inCurrentNewVisit = null;
  
  /**
   * local variable to track first pageview in order to suppress an action below
   * @type {boolean}
   */
  let _lastView = null;

  /**
   * number of milliseconds before accepting a new visitview
   * @type {boolean}
   */
  let _lastViewWaitMilliseconds = 1000;
  
  /*==FUNCTIONS================================================*/ 
  
  /**
   * This is a "private" method that returns minutes from epoch
   * @private
   */
  let _minutesFromEpoch = function() {
    return Math.floor((+new Date()) / 60000);
  };
  
  /**
   * This is a public method for identifying new user visits. It makes use of a 
   * private property "_inCurrentNewVisit" to store this status, and only updates
   * this status on pageview events that trigger the visitView method.
   * 
   * @param ignoreFlag {boolean}  Where or not to use the saved _inCurrentNewVisit flag  
   * @public
   */
  isNewVisit = function(ignoreFlag){
    let sessCookie = cookie.getJson(cookieName);
    
    // If flag is set, return that value _unless_ ignoreFlag is true
    if (typeof _inCurrentNewVisit == 'boolean' && !ignoreFlag) {
      return _inCurrentNewVisit;
    }
    
    // if there is a visit timestamp (vtime), let's compare our timestamp 
    // to minuesFromEpoch. If we are under 30 minutes, we're still in an 
    // existing visit
    if (sessCookie.vtime && !isNaN(sessCookie.vtime) && (_minutesFromEpoch() - sessCookie.vtime) <= lengthOfSessionInMinutes) {
      return false;
    } else {
      // cookie's not set - it's a new visit
      return true;
    }
  };
  
  /**
   * This is the public interface for connecting the Omniture pageview event
   * with this object - it updates timestamp saved in the session cookie
   *   
   * @public
   **/
  visitView = function(){
    let sessCookie = cookie.getJson(cookieName); 
    
    // if time since last view isn't over lastViewWaitMilliseconds, wait
    // don't register the view.
    if (_lastView && ((+new Date()) - _lastView) <= _lastViewWaitMilliseconds ) {
      // if it's been too short a time since our last check, skip
      return false;
    } else {
      _inCurrentNewVisit = isNewVisit(true);
      _lastView = (+new Date());
    }
    
    debug('webmd.visit: ' + _inCurrentNewVisit);
    
    // go ahead and update the visit timestamp (vtime) to current time as we 
    // just had a pageview
    sessCookie.vtime = _minutesFromEpoch();

    // sets the cookie with the additional timestamp data. Makes it across 
    // the entire current domain so its accessible anywhere on whatever.com
    cookie.setJson(cookieName, sessCookie, {domain: getSLD()});
  };

  /**
   * The init function will bind to the before pageview event and when 
   * that event is triggered we will update the session data in the cookie. 
   * 
   * @public
   **/
  let init = function(){
    _inCurrentNewVisit = isNewVisit();
    
    // bind to the before_pv and before_adrefresh events to go ahead and update 
    // the timestamp. This will happen on the real page view as well as any 
    // dynamic page views that might occur during the usage of this page/
    // application
    document.addEventListener('before_pv', function() {
      visitView();
    });

    document.addEventListener('before_adrefresh', function() {
      if (window.webmd.ads.initialLoadDisabled) {
        if(typeof(window.firstAdReceived) != 'undefined' && window.firstAdReceived === true){
            visitView();
          }
      }
      else{
        visitView();
      }
    });
  }

  init();
}